import * as React from 'react';
import { cn, getTestId } from '../../utils';
import { useTranslation } from '../../providers/UIProvider';
import { Button } from '../Button';

type ReorderAction = 'up' | 'down';
type ItemAction = ReorderAction | 'remove';

/** What to focus once the list has re-rendered after an action. */
type FocusTarget = { key: string; action: ItemAction } | { action: 'add' };

export interface ReorderableListProps<T = unknown> {
  /** The items in their current order; the first one is the main item. */
  items: readonly T[];
  /** Stable identity of an item, kept while it moves. */
  getKey: (item: T) => string;
  /** Heading of an item, also used to name its move and remove buttons. */
  getItemLabel: (item: T, index: number) => string;
  /** The item's own fields. */
  renderItem: (item: T, index: number) => React.ReactNode;
  /** Receives the whole list after an add, a remove or a move. */
  onChange: (next: T[]) => void;
  /** A new, empty item for the add button. */
  createItem: () => T;
  /** Shown next to the first item's heading, e.g. a "main" badge. */
  mainMarker?: React.ReactNode;
  /** Label of the add button; defaults to a generic "Add". */
  addLabel?: string;
  /** Shown while the list is empty. */
  emptyText?: React.ReactNode;
  /** The add button is disabled once the list holds this many items. */
  maxItems?: number;
  disabled?: boolean;
  className?: string;
  id?: string;
  testId?: string;
}

/** Copy of `items` with the item at `from` moved to `to`. */
function move<T>(items: readonly T[], from: number, to: number): T[] {
  const next = [...items];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
}

const ReorderableListInner = React.forwardRef<HTMLDivElement, ReorderableListProps>(
  (
    {
      items,
      getKey,
      getItemLabel,
      renderItem,
      onChange,
      createItem,
      mainMarker,
      addLabel,
      emptyText,
      maxItems,
      disabled = false,
      className,
      id,
      testId
    },
    ref
  ) => {
    const t = useTranslation('components.reorderableList');
    const rootTestId = getTestId({ testId, id });
    const listRef = React.useRef<HTMLOListElement>(null);
    const addRef = React.useRef<HTMLButtonElement>(null);
    /** The button to focus once the list has re-rendered after a move or a remove. */
    const pendingFocus = React.useRef<FocusTarget | null>(null);
    /** Read out by the live region after a move. */
    const [announcement, setAnnouncement] = React.useState('');
    const atLimit = maxItems !== undefined && items.length >= maxItems;

    React.useEffect(() => {
      const target = pendingFocus.current;
      if (!target) return;
      pendingFocus.current = null;
      if (target.action === 'add') {
        addRef.current?.focus();
        return;
      }
      const buttonOf = (action: ItemAction) =>
        Array.from(
          listRef.current?.querySelectorAll<HTMLButtonElement>('[data-reorder-action]') ?? []
        ).find(
          button =>
            button.dataset.reorderKey === target.key && button.dataset.reorderAction === action
        );
      const same = buttonOf(target.action);
      if (target.action === 'remove' || !same || !same.disabled) {
        same?.focus();
        return;
      }
      // At either end the same direction is disabled, so focus falls back to the other one.
      buttonOf(target.action === 'up' ? 'down' : 'up')?.focus();
    });

    const handleMove = (index: number, action: ReorderAction) => {
      const to = action === 'up' ? index - 1 : index + 1;
      if (to < 0 || to >= items.length) return;
      pendingFocus.current = { key: getKey(items[index]), action };
      setAnnouncement(t('moved', { item: getItemLabel(items[index], to), n: to + 1 }));
      onChange(move(items, index, to));
    };

    const handleRemove = (index: number) => {
      // Focus moves to the item taking this place, else the one before it, else Add.
      const neighbour = items[index + 1] ?? items[index - 1];
      pendingFocus.current = neighbour
        ? { key: getKey(neighbour), action: 'remove' }
        : { action: 'add' };
      onChange(items.filter((_, i) => i !== index));
    };

    const handleAdd = () => {
      if (atLimit) return;
      onChange([...items, createItem()]);
    };

    return (
      <div
        ref={ref}
        id={id}
        data-testid={rootTestId}
        className={cn('flex flex-col gap-sm', className)}
      >
        <p role='status' aria-live='polite' className='sr-only'>
          {announcement}
        </p>

        {items.length === 0 && emptyText ? (
          <p className='text-sm text-foreground-muted'>{emptyText}</p>
        ) : null}

        {items.length > 0 && (
          <ol ref={listRef} className='flex flex-col gap-sm'>
            {items.map((item, index) => {
              const key = getKey(item);
              const label = getItemLabel(item, index);
              return (
                <li
                  key={key}
                  data-testid={rootTestId ? `${rootTestId}-item-${index}` : undefined}
                  className='flex flex-col gap-sm rounded-lg border border-border-base p-md'
                >
                  <div className='flex items-center gap-sm'>
                    <span className='min-w-0 truncate text-sm font-semibold text-foreground-base'>
                      {label}
                    </span>
                    {index === 0 ? mainMarker : null}
                    <div className='ml-auto flex shrink-0 items-center gap-xs'>
                      <Button
                        variant='ghost'
                        size='icon'
                        icon='arrow-up'
                        aria-label={t('moveUp', { item: label })}
                        title={t('moveUp', { item: label })}
                        data-reorder-key={key}
                        data-reorder-action='up'
                        disabled={disabled || index === 0}
                        onClick={() => handleMove(index, 'up')}
                      />
                      <Button
                        variant='ghost'
                        size='icon'
                        icon='arrow-down'
                        aria-label={t('moveDown', { item: label })}
                        title={t('moveDown', { item: label })}
                        data-reorder-key={key}
                        data-reorder-action='down'
                        disabled={disabled || index === items.length - 1}
                        onClick={() => handleMove(index, 'down')}
                      />
                      <Button
                        variant='ghost'
                        size='icon'
                        icon='trash'
                        className='text-error-text'
                        aria-label={t('remove', { item: label })}
                        title={t('remove', { item: label })}
                        data-reorder-key={key}
                        data-reorder-action='remove'
                        disabled={disabled}
                        onClick={() => handleRemove(index)}
                      />
                    </div>
                  </div>
                  {renderItem(item, index)}
                </li>
              );
            })}
          </ol>
        )}

        <div className='flex flex-wrap items-center gap-sm'>
          <Button
            ref={addRef}
            variant='default'
            outline='default'
            size='sm'
            icon='plus'
            disabled={disabled || atLimit}
            onClick={handleAdd}
          >
            {addLabel ?? t('add')}
          </Button>
          {atLimit ? (
            <span className='text-sm text-foreground-muted'>
              {t('limitReached', { max: maxItems })}
            </span>
          ) : null}
        </div>
      </div>
    );
  }
);

ReorderableListInner.displayName = 'ReorderableList';

/**
 * Editable list with add, remove and move up/down; the first item is the main one and carries
 * `mainMarker`.
 */
// forwardRef drops the item type parameter; the cast restores it for callers (as SimpleTable).
export const ReorderableList = ReorderableListInner as <T = unknown>(
  props: ReorderableListProps<T> & React.RefAttributes<HTMLDivElement>
) => React.ReactElement | null;
